import { useCallback } from 'react';
import {
  FormStepNode,
  emptyStepSingle,
  emptyStepGroup,
} from '../workoutBuilderTypes';

interface UseConditioningStepsReturn {
  addSingle: () => void;
  addGroup: () => void;
  removeNode: (localId: string) => void;
  updateNode: (localId: string, patch: Partial<FormStepNode>) => void;
  addChild: (groupLocalId: string) => void;
  removeChild: (groupLocalId: string, childLocalId: string) => void;
  updateChild: (groupLocalId: string, childLocalId: string, patch: Partial<FormStepNode>) => void;
}

export function useConditioningSteps(
  steps: FormStepNode[],
  onChange: (s: FormStepNode[]) => void,
): UseConditioningStepsReturn {
  const addSingle = useCallback(() => {
    onChange([...steps, emptyStepSingle()]);
  }, [steps, onChange]);

  const addGroup = useCallback(() => {
    onChange([...steps, emptyStepGroup()]);
  }, [steps, onChange]);

  const removeNode = useCallback(
    (localId: string) => {
      onChange(steps.filter((n) => n._localId !== localId));
    },
    [steps, onChange],
  );

  const updateNode = useCallback(
    (localId: string, patch: Partial<FormStepNode>) => {
      onChange(
        steps.map((n) => (n._localId === localId ? { ...n, ...patch } : n)),
      );
    },
    [steps, onChange],
  );

  const addChild = useCallback(
    (groupLocalId: string) => {
      onChange(
        steps.map((n) => {
          if (n._localId !== groupLocalId) return n;
          return { ...n, children: [...n.children, emptyStepSingle()] };
        }),
      );
    },
    [steps, onChange],
  );

  const removeChild = useCallback(
    (groupLocalId: string, childLocalId: string) => {
      onChange(
        steps.map((n) => {
          if (n._localId !== groupLocalId) return n;
          return {
            ...n,
            children: n.children.filter((c) => c._localId !== childLocalId),
          };
        }),
      );
    },
    [steps, onChange],
  );

  const updateChild = useCallback(
    (groupLocalId: string, childLocalId: string, patch: Partial<FormStepNode>) => {
      onChange(
        steps.map((n) => {
          if (n._localId !== groupLocalId) return n;
          return {
            ...n,
            children: n.children.map((c) =>
              c._localId === childLocalId ? { ...c, ...patch } : c,
            ),
          };
        }),
      );
    },
    [steps, onChange],
  );

  return {
    addSingle,
    addGroup,
    removeNode,
    updateNode,
    addChild,
    removeChild,
    updateChild,
  };
}
